import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  CreditCard,
  Moon,
  Sun,
  Upload,
  Download,
  Settings,
  X,
  Trash2,
  Plus,
  Save,
  Clock,
  Calendar,
  AlertCircle,
  HelpCircle,
  Menu,
  QrCode,
} from "lucide-react";

function loadScript(src) {
  return new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
    const s = document.createElement("script");
    s.src = src; s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
}
loadScript("https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js").catch(() => { });
const jsQRLoadPromise = loadScript("https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js");
jsQRLoadPromise.catch(() => { });

// ── Cloud session store (Vercel + MongoDB, shares splitly's DB, own collection) ──
const SESSION_API = "https://splitly-api-psi.vercel.app/api/cardy-session";

async function storeSessionCloud(data) {
  const res = await fetch(SESSION_API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to store session");
  const { key } = await res.json();
  return key;
}

const SESSION_KEY_PATTERN = /^[A-Za-z0-9_-]{6,12}$/;

async function fetchSessionCloud(key) {
  if (typeof key !== "string" || !SESSION_KEY_PATTERN.test(key)) {
    throw new Error("That doesn't look like a valid session key.");
  }
  const res = await fetch(`${SESSION_API}/${encodeURIComponent(key)}`);
  if (!res.ok) throw new Error("Session not found");
  return res.json();
}

function decodeQRImage(file) {
  return new Promise((resolve, reject) => {
    jsQRLoadPromise
      .catch(() => { throw new Error("Could not load the QR scanning library. Check your connection."); })
      .then(() => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const result = window.jsQR
            ? window.jsQR(imageData.data, imageData.width, imageData.height)
            : null;
          URL.revokeObjectURL(img.src);
          if (result?.data) resolve(result.data);
          else reject(new Error("No QR code found in image."));
        };
        img.onerror = () => reject(new Error("Could not read image."));
        img.src = URL.createObjectURL(file);
      })
      .catch(reject);
  });
}

function scanQRFromVideo(video, canvas, onResult, onError) {
  let stopped = false;
  let lastAttempt = 0;
  const MIN_INTERVAL_MS = 150;
  const MAX_DIM = 480;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const tick = (t) => {
    if (stopped) return;
    if (video.readyState === video.HAVE_ENOUGH_DATA && window.jsQR && t - lastAttempt >= MIN_INTERVAL_MS) {
      lastAttempt = t;
      const scale = Math.min(1, MAX_DIM / Math.max(video.videoWidth, video.videoHeight));
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const result = window.jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });
      if (result?.data) { stopped = true; onResult(result.data); return; }
    }
    requestAnimationFrame(tick);
  };
  jsQRLoadPromise.then(() => { if (!stopped) requestAnimationFrame(tick); }).catch(onError);
  return () => { stopped = true; };
}

function Spinner({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" style={{ display: "inline-block", verticalAlign: "middle" }}>
      <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" strokeDasharray="85 60">
        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Palette (from style.md — shared okaystephen.github.io app design system)
// ---------------------------------------------------------------------------
const palette = {
  light: {
    bg: "#f5f7f4", surface: "#ffffff", surfaceAlt: "#eef1ec",
    border: "#d4dcd0", borderStrong: "#b0bfaa",
    text: "#1e2b1a", textMuted: "#5a7052",
    accent: "#5a8a5e", accentHover: "#4a7a4e", accentSoft: "#d8ead9", accentText: "#ffffff",
    danger: "#c0392b", dangerSoft: "#fdecea",
    shadow: "0 2px 12px rgba(90,138,94,0.10)", shadowLg: "0 8px 32px rgba(90,138,94,0.13)",
    tag: "#e3ede3", tagText: "#3a6b3f",
  },
  dark: {
    bg: "#141a13", surface: "#1e2b1a", surfaceAlt: "#253021",
    border: "#2e3f29", borderStrong: "#3d5437",
    text: "#dcebd8", textMuted: "#7da87a",
    accent: "#6aaa6e", accentHover: "#7aba7e", accentSoft: "#1e3320", accentText: "#0e1a0d",
    danger: "#e05c4e", dangerSoft: "#2d1512",
    shadow: "0 2px 12px rgba(0,0,0,0.35)", shadowLg: "0 8px 32px rgba(0,0,0,0.45)",
    tag: "#253021", tagText: "#7da87a",
  },
};

// ---------------------------------------------------------------------------
// Date math — figures out, for a purchase made today, when the statement
// that will include it closes, and when payment on that statement is due.
// ---------------------------------------------------------------------------
function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function nextStatementClose(today, statementDay) {
  const y = today.getFullYear();
  const m = today.getMonth();
  const dThis = Math.min(statementDay, daysInMonth(y, m));
  const closeThis = new Date(y, m, dThis);
  if (today.getTime() <= closeThis.getTime()) return closeThis;
  const dNext = Math.min(statementDay, daysInMonth(y, m + 1));
  return new Date(y, m + 1, dNext);
}

function dueDateFor(closeDate, dueDay, dueMonthOffset) {
  const y = closeDate.getFullYear();
  const m = closeDate.getMonth() + dueMonthOffset;
  const d = Math.min(dueDay, daysInMonth(y, m));
  return new Date(y, m, d);
}

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function fmtDate(d) {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function toInputValue(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function fromInputValue(str) {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function computeRanking(cards, referenceDate) {
  const today = startOfDay(referenceDate || new Date());
  return cards
    .map((card) => {
      const close = nextStatementClose(today, card.statementDay);
      const due = dueDateFor(close, card.dueDay, card.dueMonthOffset);
      const deferralDays = Math.round((due.getTime() - today.getTime()) / 86400000);
      return { ...card, statementClose: close, dueDate: due, deferralDays };
    })
    .sort((a, b) => b.deferralDays - a.deferralDays);
}

function emptyDraftCard() {
  return {
    id: `card-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    bank: "",
    cardName: "",
    statementDay: 1,
    dueDay: 1,
    dueMonthOffset: 1,
  };
}

// ── QR Export/Import Modal ──────────────────────────────────────────────
function QRModal({ mode, c, onClose, onExportRequest, onImportKey, summary }) {
  const qrHostRef = useRef(null);
  const [key, setKey] = useState(null);
  const [status, setStatus] = useState(mode === "export" ? "loading" : "idle");
  const [manualKey, setManualKey] = useState("");
  const [importing, setImporting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const requestedRef = useRef(false);
  const manualKeyInputRef = useRef(null);

  useEffect(() => {
    if (mode === "import") manualKeyInputRef.current?.focus();
  }, [mode]);

  const [errorText, setErrorText] = useState("");

  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [showTapFallback, setShowTapFallback] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const stopScanRef = useRef(null);
  const tapFallbackTimerRef = useRef(null);

  const stopCamera = () => {
    stopScanRef.current?.();
    stopScanRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    clearTimeout(tapFallbackTimerRef.current);
    setScanning(false);
    setVideoPlaying(false);
    setShowTapFallback(false);
  };

  const [closing, setClosing] = useState(false);
  const closeTimerRef = useRef(null);
  const CLOSE_MS = 200;

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const requestClose = () => {
    if (closing) return;
    stopCamera();
    setClosing(true);
    closeTimerRef.current = setTimeout(onClose, CLOSE_MS);
  };

  useEffect(() => stopCamera, []);

  const startCamera = async () => {
    setCameraError("");
    setErrorMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
      });
      streamRef.current = stream;
      setScanning(true);
    } catch (err) {
      setCameraError(err.name === "NotAllowedError" ? "Camera permission denied." : "Could not access camera.");
    }
  };

  const onImportKeyRef = useRef(onImportKey);
  useEffect(() => { onImportKeyRef.current = onImportKey; }, [onImportKey]);

  useEffect(() => {
    if (!scanning || !streamRef.current || !videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    video.muted = true;
    video.setAttribute("muted", "");
    video.setAttribute("webkit-playsinline", "true");
    video.srcObject = streamRef.current;

    const tryPlay = () => video.play().catch(() => { });
    const onPlaying = () => { setVideoPlaying(true); setShowTapFallback(false); clearTimeout(tapFallbackTimerRef.current); };
    video.addEventListener("playing", onPlaying);
    video.addEventListener("canplay", tryPlay);
    tryPlay();

    tapFallbackTimerRef.current = setTimeout(() => {
      if (video.paused) setShowTapFallback(true);
    }, 1200);

    stopScanRef.current = scanQRFromVideo(
      video,
      canvasRef.current,
      async (decoded) => {
        stopCamera();
        setImporting(true);
        try {
          await onImportKeyRef.current(decoded.trim());
          requestClose();
        } catch (err) {
          setErrorMsg(err.message || "Could not read QR code.");
        } finally {
          setImporting(false);
        }
      },
      () => setCameraError("Could not load the QR scanning library. Check your connection.")
    );
    return () => {
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("canplay", tryPlay);
      clearTimeout(tapFallbackTimerRef.current);
      stopScanRef.current?.();
      stopScanRef.current = null;
    };
  }, [scanning]);

  useEffect(() => {
    if (mode !== "export" || requestedRef.current) return;
    requestedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const k = await onExportRequest();
        if (cancelled) return;
        setKey(k);
        setStatus("ready");
      } catch (err) {
        if (!cancelled) {
          setErrorText(err.message || "Could not upload session. Check your connection and try again.");
          setStatus("error");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [mode, onExportRequest]);

  useEffect(() => {
    if (status !== "ready" || !key || !qrHostRef.current || !window.QRCode) return;
    qrHostRef.current.innerHTML = "";
    new window.QRCode(qrHostRef.current, { text: key, width: 200, height: 200 });
  }, [status, key]);

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setErrorMsg("");
    try {
      const decoded = await decodeQRImage(file);
      await onImportKey(decoded.trim());
      requestClose();
    } catch (err) {
      setErrorMsg(err.message || "Could not read QR code.");
    } finally {
      setImporting(false);
    }
  };

  const handleManualSubmit = async () => {
    const k = manualKey.trim();
    if (!k) return;
    setImporting(true);
    setErrorMsg("");
    try {
      await onImportKey(k);
      requestClose();
    } catch (err) {
      setErrorMsg(err.message || "Session not found.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div onClick={requestClose} className={`qr-modal-overlay${closing ? " closing" : ""}`} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 500,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} className={`qr-modal-panel${closing ? " closing" : ""}`} style={{
        background: c.surface, borderRadius: 16, padding: 24, maxWidth: 340, width: "100%",
        boxShadow: c.shadowLg, fontFamily: "'Space Grotesk', sans-serif", color: c.text,
      }}>
        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 16 }}>
          {mode === "export" ? "Export via QR" : "Import via QR"}
        </div>

        {mode === "export" && (
          <>
            {status === "loading" && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "24px 0", color: c.textMuted }}>
                <Spinner size={22} color={c.textMuted} />
                Uploading cards…
              </div>
            )}
            {status === "error" && <div style={{ color: c.danger, fontSize: 13 }}>{errorText}</div>}
            {status === "ready" && (
              <div style={{ textAlign: "center" }}>
                {summary && (
                  <div style={{
                    textAlign: "left", background: c.surfaceAlt, border: `1.5px solid ${c.border}`,
                    borderRadius: 10, padding: "10px 12px", marginBottom: 14, fontSize: 12.5, color: c.text,
                  }}>
                    <b style={{ color: c.text }}>Cards:</b> {summary}
                  </div>
                )}
                <div ref={qrHostRef} style={{ display: "inline-block", padding: 12, background: "#fff", borderRadius: 12 }} />
                <div style={{ marginTop: 12, fontSize: 13, color: c.textMuted }}>Scan this on another device to import.</div>
                <div style={{ marginTop: 8, fontSize: 12, fontFamily: "monospace", color: c.textMuted, wordBreak: "break-all" }}>{key}</div>
              </div>
            )}
          </>
        )}

        {mode === "import" && (
          <>
            {scanning ? (
              <div style={{ marginBottom: 12 }}>
                <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", background: "#000", aspectRatio: "4 / 3" }}>
                  <video ref={videoRef} playsInline autoPlay muted style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <canvas ref={canvasRef} style={{ display: "none" }} />
                  {!videoPlaying && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, color: "#fff" }}>
                      {showTapFallback ? (
                        <button onClick={() => videoRef.current?.play().catch(() => { })} style={{
                          padding: "9px 16px", borderRadius: 10, border: "1.5px solid #fff", background: "rgba(255,255,255,0.1)",
                          color: "#fff", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: 13, cursor: "pointer",
                        }}>Tap to start camera preview</button>
                      ) : (
                        <>
                          <Spinner size={26} color="#fff" />
                          <div style={{ fontSize: 12 }}>Starting camera…</div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <button onClick={stopCamera} style={{
                  width: "100%", marginTop: 8, padding: "9px 14px", borderRadius: 10, border: `1.5px solid ${c.border}`,
                  background: "none", color: c.textMuted, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600,
                  fontSize: 13, cursor: "pointer",
                }}>Stop scanning</button>
              </div>
            ) : (
              <button onClick={startCamera} disabled={importing} style={{
                width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${c.border}`,
                background: c.accent, color: "#fff", fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
                fontSize: 14, cursor: importing ? "not-allowed" : "pointer", marginBottom: 8,
              }}>Scan with camera</button>
            )}
            {cameraError && <div style={{ fontSize: 12, color: c.danger, marginBottom: 8 }}>{cameraError}</div>}

            <input id="cardy-qr-import-file" type="file" accept="image/*" style={{ display: "none" }} onChange={handleFilePick} />
            <button onClick={() => document.getElementById("cardy-qr-import-file").click()} disabled={importing} style={{
              width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${c.border}`,
              background: c.surfaceAlt, color: c.text, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600,
              fontSize: 14, cursor: importing ? "not-allowed" : "pointer", marginBottom: 12,
            }}>Upload QR image instead</button>

            <div style={{ fontSize: 12, color: c.textMuted, textAlign: "center", margin: "4px 0 12px" }}>or enter key manually</div>

            <div style={{ display: "flex", gap: 8 }}>
              <input
                ref={manualKeyInputRef}
                onChange={(e) => setManualKey(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleManualSubmit(); }}
                placeholder="e.g. aB3xY9kP"
                style={{
                  flex: 1, padding: "9px 12px", borderRadius: 10, border: `1.5px solid ${c.border}`,
                  background: c.surfaceAlt, color: c.text, fontFamily: "monospace", fontSize: 14,
                }} />
              <button onClick={handleManualSubmit} disabled={importing || !manualKey.trim()} style={{
                padding: "9px 16px", borderRadius: 10, border: "none", background: c.accent,
                color: "#fff", fontWeight: 700, fontSize: 14, cursor: importing ? "not-allowed" : "pointer",
              }}>Go</button>
            </div>

            {importing && (
              <div style={{ marginTop: 12, fontSize: 13, color: c.textMuted, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <Spinner size={15} color={c.textMuted} />
                Importing…
              </div>
            )}
            {errorMsg && <div style={{ marginTop: 12, fontSize: 13, color: c.danger }}>{errorMsg}</div>}
          </>
        )}

        <button onClick={requestClose} style={{
          marginTop: 18, width: "100%", padding: "9px", borderRadius: 10, border: `1.5px solid ${c.border}`,
          background: "none", color: c.textMuted, fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600,
          fontSize: 13, cursor: "pointer",
        }}>Close</button>
      </div>
    </div>
  );
}

export default function Cardy() {
  const [dark, setDark] = useState(() => {
    const hour = new Date().getHours();
    return hour >= 19 || hour < 6;
  });
  const [fontSize] = useState(14);
  const [cards, setCards] = useState([]);
  const [fileName, setFileName] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [draft, setDraft] = useState([]);
  const [error, setError] = useState(null);
  const [refDate, setRefDate] = useState(() => startOfDay(new Date()));
  const [qrModalMode, setQrModalMode] = useState(null); // null | "export" | "import"

  const menuRef = useRef(null);
  const c = dark ? palette.dark : palette.light;

  // Close the hamburger menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  // Load Space Grotesk
  useEffect(() => {
    if (!document.getElementById("cardy-font")) {
      const link = document.createElement("link");
      link.id = "cardy-font";
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  // Inject styles (per style.md pattern)
  useEffect(() => {
    const id = "cardy-styles";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("style");
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = `
      .cardy-root {
        font-family: 'Space Grotesk', sans-serif;
        background: ${c.bg};
        color: ${c.text};
        min-height: 100vh;
        transition: background 0.4s ease, color 0.4s ease;
      }
      .cardy-root * { box-sizing: border-box; font-family: inherit; }
      .card {
        background: ${c.surface};
        border: 1.5px solid ${c.border};
        border-radius: 16px;
        box-shadow: ${c.shadow};
      }
      .cardy-input, .cardy-select {
        background: ${c.surfaceAlt};
        border: 1.5px solid ${c.border};
        border-radius: 10px;
        padding: 8px 12px;
        color: ${c.text};
        font-size: ${fontSize}px;
        outline: none;
        transition: border-color 0.2s ease;
        width: 100%;
      }
      .cardy-input:focus, .cardy-select:focus { border-color: ${c.accent}; }
      .btn-primary {
        background: ${c.accent};
        color: ${c.accentText};
        border: none;
        border-radius: 10px;
        padding: 9px 18px;
        font-weight: 600;
        font-size: ${fontSize}px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
      }
      .btn-primary:hover { background: ${c.accentHover}; box-shadow: 0 4px 14px ${c.accentSoft}; }
      .btn-primary:active { transform: scale(0.97); }
      .btn-primary:disabled { opacity: 0.38; cursor: not-allowed; }
      .btn-ghost {
        background: transparent;
        color: ${c.textMuted};
        border: 1.5px solid ${c.border};
        border-radius: 10px;
        padding: 9px 18px;
        font-weight: 600;
        font-size: ${fontSize}px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        transition: all 0.2s ease;
      }
      .btn-ghost:hover { border-color: ${c.accent}; color: ${c.accent}; background: ${c.accentSoft}; }
      .btn-ghost:active { transform: scale(0.97); }
      .btn-danger {
        background: transparent;
        color: ${c.danger};
        border: none;
        border-radius: 6px;
        padding: 6px 10px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        transition: background 0.2s ease;
      }
      .btn-danger:hover { background: ${c.dangerSoft}; }
      .tag {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background: ${c.tag};
        color: ${c.tagText};
        border-radius: 6px;
        padding: 3px 8px;
        font-size: ${fontSize - 2}px;
        font-weight: 600;
      }
      .empty-state {
        text-align: center;
        color: ${c.textMuted};
        padding: 48px 24px;
      }
      .divider { border-top: 1.5px solid ${c.border}; margin: 0; }
      .fade-slide { animation: fadeSlide 0.24s ease; }
      @keyframes fadeSlide {
        from { opacity: 0; transform: translateY(10px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .qr-modal-overlay { animation: qrOverlayIn 0.2s ease; }
      .qr-modal-overlay.closing { animation: qrOverlayOut 0.2s ease forwards; }
      .qr-modal-panel { animation: qrPanelIn 0.2s cubic-bezier(0.16, 1, 0.3, 1); }
      .qr-modal-panel.closing { animation: qrPanelOut 0.2s ease forwards; }
      @keyframes qrOverlayIn { from { opacity: 0; } to { opacity: 1; } }
      @keyframes qrOverlayOut { from { opacity: 1; } to { opacity: 0; } }
      @keyframes qrPanelIn {
        from { opacity: 0; transform: scale(0.94) translateY(8px); }
        to   { opacity: 1; transform: scale(1) translateY(0); }
      }
      @keyframes qrPanelOut {
        from { opacity: 1; transform: scale(1) translateY(0); }
        to   { opacity: 0; transform: scale(0.94) translateY(8px); }
      }
      .rank-row {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px;
        border-radius: 14px;
        border: 1.5px solid ${c.border};
        background: ${c.surface};
        transition: border-color 0.2s ease, transform 0.2s ease;
      }
      .rank-row:hover { border-color: ${c.borderStrong}; }
      .rank-badge {
        width: 34px;
        height: 34px;
        min-width: 34px;
        border-radius: 50%;
        background: ${c.accentSoft};
        color: ${c.accent};
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: ${fontSize}px;
      }
      .rank-row.top .rank-badge { background: ${c.accent}; color: ${c.accentText}; }
      .cardy-modal-overlay {
        position: fixed; inset: 0;
        background: rgba(0,0,0,0.45);
        display: flex; align-items: center; justify-content: center;
        z-index: 1000;
        padding: 20px;
      }
      .cardy-modal {
        background: ${c.surface};
        border: 1.5px solid ${c.border};
        border-radius: 16px;
        box-shadow: ${c.shadowLg};
        width: 100%;
        max-width: 640px;
        max-height: 85vh;
        display: flex;
        flex-direction: column;
      }
      .modal-header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 18px 20px;
        border-bottom: 1.5px solid ${c.border};
      }
      .modal-body { padding: 20px; overflow-y: auto; }
      .modal-footer {
        display: flex; align-items: center; justify-content: space-between;
        gap: 10px;
        padding: 16px 20px;
        border-top: 1.5px solid ${c.border};
      }
      .draft-row {
        border: 1.5px solid ${c.border};
        border-radius: 12px;
        padding: 12px;
        margin-bottom: 10px;
        background: ${c.surfaceAlt};
      }
      .field-label {
        font-size: ${fontSize - 3}px;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: ${c.textMuted};
        margin-bottom: 4px;
        display: block;
      }
      ::-webkit-scrollbar { width: 6px; }
      ::-webkit-scrollbar-track { background: ${c.bg}; }
      ::-webkit-scrollbar-thumb { background: ${c.borderStrong}; border-radius: 3px; }
      .icon-btn {
        background: transparent;
        border: none;
        cursor: pointer;
        color: ${c.textMuted};
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px; height: 36px;
        border-radius: 10px;
        transition: background 0.2s ease, color 0.2s ease;
      }
      .icon-btn:hover { background: ${c.accentSoft}; color: ${c.accent}; }
      .help-step {
        display: flex;
        gap: 12px;
        margin-bottom: 16px;
      }
      .help-step-badge {
        width: 26px; height: 26px; min-width: 26px;
        border-radius: 50%;
        background: ${c.accentSoft};
        color: ${c.accent};
        display: flex; align-items: center; justify-content: center;
        font-weight: 700;
        font-size: ${fontSize - 2}px;
      }
      .help-step-title { font-weight: 700; margin-bottom: 2px; }
      .help-step-body { color: ${c.textMuted}; font-size: ${fontSize - 1}px; line-height: 1.5; }
      .help-code {
        background: ${c.surfaceAlt};
        border: 1.5px solid ${c.border};
        border-radius: 8px;
        padding: 10px 12px;
        font-size: ${fontSize - 2}px;
        color: ${c.text};
        overflow-x: auto;
        margin-top: 6px;
        white-space: pre;
      }
      .menu-wrap { position: relative; }
      .menu-dropdown {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        background: ${c.surface};
        border: 1.5px solid ${c.border};
        border-radius: 12px;
        box-shadow: ${c.shadowLg};
        padding: 6px;
        min-width: 180px;
        z-index: 100;
      }
      .menu-item {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        background: transparent;
        border: none;
        border-radius: 8px;
        padding: 9px 10px;
        font-size: ${fontSize}px;
        font-weight: 600;
        color: ${c.text};
        cursor: pointer;
        text-align: left;
        transition: background 0.2s ease;
      }
      .menu-item:hover { background: ${c.accentSoft}; color: ${c.accent}; }
      .menu-item:disabled { opacity: 0.38; cursor: not-allowed; }
      .menu-item:disabled:hover { background: transparent; color: ${c.text}; }
      .menu-item-danger { color: ${c.danger}; }
      .menu-item-danger:hover { background: ${c.dangerSoft}; color: ${c.danger}; }
      .menu-item-danger:disabled:hover { background: transparent; color: ${c.danger}; }
    `;
  }, [c, fontSize]);

  const ranking = useMemo(() => computeRanking(cards, refDate), [cards, refDate]);
  const isToday = useMemo(() => isSameDay(refDate, startOfDay(new Date())), [refDate]);

  function clearCards() {
    if (!window.confirm("Clear all cards? This can't be undone.")) return;
    setCards([]);
    setFileName(null);
    setError(null);
  }

  function handleExport() {
    const payload = { cards };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "cardy-cards.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function handleQRExportRequest() {
    const key = await storeSessionCloud({ cards });
    return key;
  }

  async function handleQRImportKey(key) {
    const data = await fetchSessionCloud(key);
    const list = Array.isArray(data) ? data : data.cards;
    if (!Array.isArray(list)) throw new Error("Session has no cards.");
    const normalized = list.map((item, i) => ({
      id: item.id || `card-${Date.now()}-${i}`,
      bank: item.bank || "",
      cardName: item.cardName || "",
      statementDay: Number(item.statementDay) || 1,
      dueDay: Number(item.dueDay) || 1,
      dueMonthOffset: Number(item.dueMonthOffset) === 1 ? 1 : 0,
    }));
    setCards(normalized);
    setFileName(null);
    setError(null);
  }

  function openModal() {
    setDraft(cards.map((cd) => ({ ...cd })));
    setShowModal(true);
  }

  function saveModal() {
    const cleaned = draft
      .filter((d) => d.bank.trim() || d.cardName.trim())
      .map((d) => ({
        ...d,
        statementDay: Math.min(31, Math.max(1, Number(d.statementDay) || 1)),
        dueDay: Math.min(31, Math.max(1, Number(d.dueDay) || 1)),
        dueMonthOffset: Number(d.dueMonthOffset) === 1 ? 1 : 0,
      }));
    setCards(cleaned);
    setShowModal(false);
  }

  function updateDraftField(id, field, value) {
    setDraft((prev) => prev.map((d) => (d.id === id ? { ...d, [field]: value } : d)));
  }

  function removeDraftRow(id) {
    setDraft((prev) => prev.filter((d) => d.id !== id));
  }

  function addDraftRow() {
    setDraft((prev) => [...prev, emptyDraftCard()]);
  }

  return (
    <div className="cardy-root">
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "28px 20px 60px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 38, height: 38, borderRadius: 10,
                background: c.accentSoft, display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <CreditCard size={20} color={c.accent} />
            </div>
            <div>
              <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: "-0.01em" }}>Cardy</div>
              <div style={{ fontSize: fontSize - 3, color: c.textMuted, fontWeight: 500 }}>
                Which card buys you the most time
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <button className="icon-btn" onClick={() => setShowHelp(true)} title="How to use">
              <HelpCircle size={18} />
            </button>
            <div className="menu-wrap" ref={menuRef}>
              <button className="icon-btn" onClick={() => setShowMenu((m) => !m)} title="Menu">
                <Menu size={18} />
              </button>
              {showMenu && (
                <div className="menu-dropdown fade-slide">
                  <button
                    className="menu-item"
                    onClick={() => {
                      setShowMenu(false);
                      openModal();
                    }}
                  >
                    <Settings size={16} /> Manage cards
                  </button>
                  <button
                    className="menu-item"
                    onClick={() => {
                      setShowMenu(false);
                      setQrModalMode("import");
                    }}
                  >
                    <QrCode size={16} /> Import via QR
                  </button>
                  <button
                    className="menu-item"
                    onClick={() => {
                      setShowMenu(false);
                      setQrModalMode("export");
                    }}
                    disabled={cards.length === 0}
                  >
                    <QrCode size={16} /> Export via QR
                  </button>
                  <button
                    className="menu-item menu-item-danger"
                    onClick={() => {
                      setShowMenu(false);
                      clearCards();
                    }}
                    disabled={cards.length === 0}
                  >
                    <Trash2 size={16} /> Clear
                  </button>
                </div>
              )}
            </div>
            <button className="icon-btn" onClick={() => setDark((d) => !d)} title="Toggle theme">
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </div>

        {error && (
          <div
            className="fade-slide"
            style={{
              display: "flex", alignItems: "center", gap: 8,
              background: c.dangerSoft, color: c.danger,
              borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: fontSize - 1,
            }}
          >
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* Ranking */}
        {cards.length === 0 ? (
          <div className="card empty-state fade-slide">
            <CreditCard size={28} color={c.textMuted} style={{ marginBottom: 10 }} />
            <div style={{ fontWeight: 700, color: c.text, marginBottom: 6 }}>No cards yet</div>
            <div style={{ fontSize: fontSize - 1, marginBottom: 16 }}>
              Open <strong>Manage cards</strong> to add your cards by hand, then export them to get
              a QR code you can scan to bring them onto another device.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              <button className="btn-primary" onClick={() => setQrModalMode("import")}>
                <Upload size={16} /> Import
              </button>
              <button className="btn-ghost" onClick={() => setShowHelp(true)}>
                <HelpCircle size={16} /> How to use
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }} className="fade-slide">
            <div
              className="card"
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 14px", flexWrap: "wrap",
              }}
            >
              <Calendar size={16} color={c.accent} />
              <span style={{ fontSize: fontSize - 1, fontWeight: 600, color: c.textMuted, whiteSpace: "nowrap" }}>
                Charging on
              </span>
              <input
                className="cardy-input"
                type="date"
                value={toInputValue(refDate)}
                onChange={(e) => {
                  if (!e.target.value) return;
                  setRefDate(startOfDay(fromInputValue(e.target.value)));
                }}
                style={{ width: "auto", flex: "1 1 150px" }}
              />
              {!isToday && (
                <button
                  className="btn-ghost"
                  style={{ padding: "6px 12px" }}
                  onClick={() => setRefDate(startOfDay(new Date()))}
                >
                  Today
                </button>
              )}
            </div>

            <div style={{ fontSize: fontSize - 3, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: c.textMuted, marginBottom: -6 }}>
              Ranked by longest deferral, if you charge something {isToday ? "today" : `on ${fmtDate(refDate)}`}
            </div>
            {ranking.map((card, i) => (
              <div key={card.id} className={`rank-row${i === 0 ? " top" : ""}`}>
                <div className="rank-badge">{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: fontSize + 1 }}>{card.bank}</div>
                  <div style={{ color: c.textMuted, fontSize: fontSize - 1, marginBottom: 6 }}>{card.cardName}</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <span className="tag">
                      <Calendar size={11} /> Statement {ordinal(card.statementDay)}
                    </span>
                    <span className="tag">
                      <Clock size={11} /> Due {fmtDate(card.dueDate)}
                    </span>
                  </div>
                </div>
                <div style={{ textAlign: "right", minWidth: 78 }}>
                  <div style={{ fontSize: fontSize + 4, fontWeight: 700, color: c.accent, lineHeight: 1.1 }}>
                    {card.deferralDays}
                  </div>
                  <div style={{ fontSize: fontSize - 3, color: c.textMuted, fontWeight: 600 }}>days</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manage modal */}
      {showModal && (
        <div className="cardy-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="cardy-modal fade-slide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ fontSize: 16, fontWeight: 700 }}>Manage cards</div>
              <button className="icon-btn" onClick={() => setShowModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              {draft.length === 0 && (
                <div style={{ color: c.textMuted, fontSize: fontSize - 1, marginBottom: 12 }}>
                  No cards yet. Add one below, or close this and import a JSON file.
                </div>
              )}
              {draft.map((d) => (
                <div key={d.id} className="draft-row">
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <span className="field-label">Bank</span>
                      <input
                        className="cardy-input"
                        value={d.bank}
                        onChange={(e) => updateDraftField(d.id, "bank", e.target.value)}
                        placeholder="e.g. UnionBank"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <span className="field-label">Card name</span>
                      <input
                        className="cardy-input"
                        value={d.cardName}
                        onChange={(e) => updateDraftField(d.id, "cardName", e.target.value)}
                        placeholder="e.g. Visa Gold"
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <span className="field-label">Statement day</span>
                      <input
                        className="cardy-input"
                        type="number"
                        min={1}
                        max={31}
                        value={d.statementDay}
                        onChange={(e) => updateDraftField(d.id, "statementDay", e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <span className="field-label">Due day</span>
                      <input
                        className="cardy-input"
                        type="number"
                        min={1}
                        max={31}
                        value={d.dueDay}
                        onChange={(e) => updateDraftField(d.id, "dueDay", e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1.3 }}>
                      <span className="field-label">Due month</span>
                      <select
                        className="cardy-select"
                        value={d.dueMonthOffset}
                        onChange={(e) => updateDraftField(d.id, "dueMonthOffset", Number(e.target.value))}
                      >
                        <option value={0}>Same month as statement</option>
                        <option value={1}>Following month</option>
                      </select>
                    </div>
                    <button className="btn-danger" onClick={() => removeDraftRow(d.id)} title="Remove card">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
              <button className="btn-ghost" onClick={addDraftRow} style={{ width: "100%", justifyContent: "center" }}>
                <Plus size={16} /> Add card
              </button>
            </div>
            <div className="modal-footer">
              <button className="btn-ghost" onClick={handleExport} disabled={cards.length === 0}>
                <Download size={16} /> Export current
              </button>
              <button className="btn-primary" onClick={saveModal}>
                <Save size={16} /> Save changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* How to use modal */}
      {showHelp && (
        <div className="cardy-modal-overlay" onClick={() => setShowHelp(false)}>
          <div className="cardy-modal fade-slide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                <HelpCircle size={18} color={c.accent} /> How to use Cardy
              </div>
              <button className="icon-btn" onClick={() => setShowHelp(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="help-step">
                <div className="help-step-badge"><HelpCircle size={14} /></div>
                <div>
                  <div className="help-step-title">What is Cardy?</div>
                  <div className="help-step-body">
                    Cardy ranks your credit cards by <strong>deferral</strong> — how many days
                    you'd have between charging a purchase and the payment actually being due. Add
                    each card's statement and due dates once, and Cardy tells you which card to use
                    today (or on any future date) to hold onto your cash the longest.
                  </div>
                </div>
              </div>

              <div className="help-step">
                <div className="help-step-badge">1</div>
                <div>
                  <div className="help-step-title">Add your cards</div>
                  <div className="help-step-body">
                    Open <strong>Manage cards</strong> from the menu and hit <strong>Add card</strong>{" "}
                    to enter your bank, card name, statement day, and due date. The same screen lets
                    you edit or remove existing cards. Changes apply once you hit{" "}
                    <strong>Save changes</strong>.
                  </div>
                </div>
              </div>

              <div className="help-step">
                <div className="help-step-badge">2</div>
                <div>
                  <div className="help-step-title">Understand the fields</div>
                  <div className="help-step-body">
                    <strong>Statement day</strong> is the day of the month your statement closes.{" "}
                    <strong>Due day</strong> is the day payment is due. <strong>Due month</strong>{" "}
                    is the same month as the statement close, or the following month.
                  </div>
                </div>
              </div>

              <div className="help-step">
                <div className="help-step-badge">3</div>
                <div>
                  <div className="help-step-title">Read the ranking</div>
                  <div className="help-step-body">
                    Cardy figures out, for a purchase made on the selected date, which statement
                    cycle it lands in and when that statement is due — then sorts every card by how
                    many days you'd have before payment is due. The top card gives you the most
                    float. Use the <strong>Charging on</strong> date picker above the list to check
                    the best card for a future purchase, not just today.
                  </div>
                </div>
              </div>

              <div className="help-step" style={{ marginBottom: 0 }}>
                <div className="help-step-badge">4</div>
                <div>
                  <div className="help-step-title">Move cards to another device</div>
                  <div className="help-step-body">
                    From the menu, <strong>Export via QR</strong> uploads your cards and shows a QR
                    code. On another device, <strong>Import via QR</strong> scans that code (or
                    accepts the key manually) to load the same cards.
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: "flex-end" }}>
              <button className="btn-primary" onClick={() => setShowHelp(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {qrModalMode && (
        <QRModal
          mode={qrModalMode}
          c={c}
          onClose={() => setQrModalMode(null)}
          onExportRequest={handleQRExportRequest}
          onImportKey={handleQRImportKey}
          summary={cards.length ? `${cards.length} card${cards.length === 1 ? "" : "s"}` : null}
        />
      )}
    </div>
  );
}